import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Sparkles, RefreshCw, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PERSONA_LABELS = {
  sales_partner:     "Sales Partner",
  sales_manager:     "Sales Manager",
  zonal_partner:     "Zonal Partner",
  regional_partner:  "Regional Partner",
  national_partner:  "National Partner",
};
const PERSONA_COLORS = {
  sales_partner:    "text-amber-600 bg-amber-500/10",
  sales_manager:    "text-emerald-600 bg-emerald-500/10",
  zonal_partner:    "text-cyan-600 bg-cyan-500/10",
  regional_partner: "text-blue-600 bg-blue-500/10",
  national_partner: "text-violet-600 bg-violet-500/10",
};

function Message({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}>
      {!isUser && (
        <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center mr-2 mt-0.5 shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
        </div>
      )}
      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${
        isUser
          ? "bg-primary text-primary-foreground rounded-br-sm"
          : "bg-card border border-border text-foreground rounded-bl-sm"
      }`}>
        {msg.content}
      </div>
    </div>
  );
}

export default function SMAIGuide() {
  const [partnerType, setPartnerType] = useState("sales_partner");
  const [partnerId, setPartnerId] = useState("");
  const [partners, setPartners] = useState([]);
  const [messages, setMessages] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => `ai-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const bottomRef = useRef(null);

  const loadSuggestions = useCallback(async (type) => {
    try {
      const r = await axios.get(`${API}/api/sm/ai-guide/suggestions`, { params: { partner_type: type }, withCredentials: true });
      setSuggestions(r.data.suggestions || []);
    } catch {}
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/sm/hierarchy`, { withCredentials: true });
      setPartners(r.data.partners || []);
    } catch {}
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);
  useEffect(() => { loadSuggestions(partnerType); setMessages([]); }, [partnerType, loadSuggestions]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setSending(true);
    try {
      const r = await axios.post(`${API}/api/sm/ai-guide/chat`, {
        message: msg,
        partner_id: partnerId || undefined,
        session_id: sessionId,
        history: messages.slice(-8),
      }, { withCredentials: true });
      setMessages(prev => [...prev, { role: "assistant", content: r.data.reply }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || "AI Guide error";
      setMessages(prev => [...prev, { role: "assistant", content: `Sorry, I encountered an error: ${detail}` }]);
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const greeting = {
    sales_partner: "Hi! I'm your personal business coach. Ask me anything about your pipeline, commissions, or how to close more deals!",
    sales_manager: "Hello! I'm your team coach. Ask me about your team's performance, who needs coaching, or how to boost conversions.",
    zonal_partner: "Welcome! I'm your zone advisor. Ask me about zone health, team performance, or expansion strategies.",
    regional_partner: "Hello! I'm your regional strategist. Ask me about regional performance, zone comparisons, or growth forecasts.",
    national_partner: "Good day! I'm your national intelligence advisor. Ask me anything about the partner network, revenue trends, or strategic expansion.",
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)]" data-testid="sm-ai-guide">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border flex items-center gap-4 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-foreground">AI Business Guide</div>
            <div className="text-[10px] text-muted-foreground">GPT-4o-mini · Your LawOS advisor</div>
          </div>
        </div>

        {/* Persona selector */}
        <div className="flex items-center gap-2 ml-auto">
          <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Persona</span>
          <select value={partnerType} onChange={e => setPartnerType(e.target.value)}
            data-testid="persona-selector"
            className="text-[12px] px-3 py-1.5 bg-card border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50">
            {Object.entries(PERSONA_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>

          {/* Test on partner data */}
          <select value={partnerId} onChange={e => setPartnerId(e.target.value)}
            className="text-[12px] px-3 py-1.5 bg-card border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary/50 max-w-[180px]">
            <option value="">— Test with partner data —</option>
            {partners.filter(p => p.partner_type === partnerType).map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>

          <button onClick={() => { setMessages([]); }}
            title="Clear conversation"
            className="p-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Persona badge */}
      <div className="px-6 py-2 border-b border-border/50 flex items-center gap-2 shrink-0">
        <span className={`text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full font-semibold ${PERSONA_COLORS[partnerType]}`}>
          {PERSONA_LABELS[partnerType]} Mode
        </span>
        <span className="text-[10px] text-muted-foreground">Platform data only · No case files or client PII</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <p className="text-[13px] text-muted-foreground max-w-sm">{greeting[partnerType]}</p>

            {/* Suggestions */}
            <div className="grid grid-cols-1 gap-2 mt-2 w-full max-w-md">
              {suggestions.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  data-testid={`suggestion-${i}`}
                  className="text-left text-[12px] px-4 py-2.5 bg-card border border-border rounded-xl text-foreground hover:bg-muted hover:border-primary/30 transition-all">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => <Message key={i} msg={m} />)}

        {sending && (
          <div className="flex justify-start mb-3">
            <div className="w-7 h-7 rounded-full bg-primary/15 flex items-center justify-center mr-2 shrink-0">
              <Sparkles className="w-3.5 h-3.5 text-primary animate-pulse" />
            </div>
            <div className="bg-card border border-border rounded-2xl rounded-bl-sm px-4 py-2.5">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-border shrink-0">
        {messages.length > 0 && suggestions.length > 0 && (
          <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
            {suggestions.slice(0, 3).map((s, i) => (
              <button key={i} onClick={() => sendMessage(s)}
                className="text-[11px] px-3 py-1.5 bg-muted border border-border rounded-full text-muted-foreground hover:text-foreground hover:bg-card whitespace-nowrap transition-all shrink-0">
                {s}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything about your business…"
            rows={1}
            data-testid="ai-guide-input"
            className="flex-1 px-4 py-2.5 bg-card border border-border rounded-xl text-[13px] resize-none focus:outline-none focus:ring-1 focus:ring-primary/50 max-h-28 overflow-y-auto"
          />
          <button onClick={() => sendMessage()} disabled={!input.trim() || sending}
            data-testid="ai-guide-send"
            className="p-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 disabled:opacity-40 transition-all shrink-0">
            <Send className="w-4 h-4" />
          </button>
        </div>
        <div className="text-[9px] text-muted-foreground mt-1.5 text-center">
          Press Enter to send · Shift+Enter for new line
        </div>
      </div>
    </div>
  );
}
